import React from 'react';
import { useMediaQuery } from 'react-responsive';
import ReactSelect from 'react-select';
import { usePatientsStore } from '../../store/patientsStore';
import { usePortalStore } from '../../../../stores/portalStore';
import { TextInput } from '../../../../shared/ui/TextInput';
import { InputAdornment, CircularProgress } from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { Tooltip } from '../../../../shared/ui/Tooltip';
import { MultipleSelect } from '../../../../shared/ui/MultipleSelect';
import { listUserCompanies } from '../../../../services/configuration/companiesService';
import styled from 'styled-components';
import { useCompaniesCatalog } from '../../../../hooks/useCompaniesCatalog';

// ─── Design tokens ────────────────────────────────────────────────────────────
const FIELD_HEIGHT = 40;
const SENSITIVE_FIELD_TOOLTIP =
  'Campos marcados podem ser revisados aqui, mas exigem solicitacao por e-mail para alteração';

// ─── Layout primitives ────────────────────────────────────────────────────────
const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9e9e9e;
  padding-bottom: 6px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 12px;
`;

const Grid = styled.div<{ cols: number }>`
  display: grid;
  grid-template-columns: repeat(${({ cols }) => cols}, 1fr);
  gap: 12px;
`;

const RequiredMark = styled.span`
  color: #d32f2f;
  margin-left: 2px;
`;

const FieldErrorText = styled.div`
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.2;
`;

// ─── ReactSelect: espelha MUI outlined size="small" ──────────────────────────
const buildSelectStyles = (disabled: boolean, hasError = false) => ({
  control: (base: object, state: { isFocused: boolean }) => ({
    ...base,
    minHeight: `${FIELD_HEIGHT}px`,
    height: `${FIELD_HEIGHT}px`,
    fontSize: '14px',
    borderRadius: '4px',
    borderColor: hasError
      ? '#d32f2f'
      : state.isFocused
      ? '#1976d2'
      : disabled
      ? 'rgba(0,0,0,0.12)'
      : 'rgba(0,0,0,0.23)',
    boxShadow: hasError
      ? '0 0 0 1px #d32f2f'
      : state.isFocused
      ? '0 0 0 1px #1976d2'
      : 'none',
    backgroundColor: disabled ? 'rgba(0,0,0,0.04)' : 'white',
    pointerEvents: disabled ? ('none' as const) : ('auto' as const),
    '&:hover': {
      borderColor: hasError
        ? '#d32f2f'
        : disabled
        ? 'rgba(0,0,0,0.12)'
        : state.isFocused
        ? '#1976d2'
        : 'rgba(0,0,0,0.87)',
    },
  }),
  valueContainer: (base: object) => ({
    ...base,
    height: `${FIELD_HEIGHT}px`,
    padding: '0 10px',
  }),
  input: (base: object) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: '14px',
  }),
  singleValue: (base: object) => ({
    ...base,
    fontSize: '14px',
    color: disabled ? 'rgba(0,0,0,0.38)' : 'rgba(0,0,0,0.87)',
  }),
  placeholder: (base: object) => ({
    ...base,
    fontSize: '14px',
    color: 'rgba(0,0,0,0.38)',
  }),
  indicatorsContainer: (base: object) => ({
    ...base,
    height: `${FIELD_HEIGHT}px`,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: object) => ({
    ...base,
    padding: '0 6px',
    color: disabled ? 'rgba(0,0,0,0.26)' : 'rgba(0,0,0,0.54)',
  }),
  clearIndicator: (base: object) => ({ ...base, padding: '0 4px' }),
  menu: (base: object) => ({ ...base, zIndex: 9999, fontSize: '14px' }),
  menuPortal: (base: object) => ({ ...base, zIndex: 9999 }),
  option: (
    base: object,
    state: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...base,
    fontSize: '14px',
    padding: '8px 12px',
    cursor: 'pointer',
    backgroundColor: state.isSelected
      ? '#e8f0fe'
      : state.isFocused
      ? '#f8f9fa'
      : 'white',
    color: state.isSelected ? '#1a73e8' : 'rgba(0,0,0,0.87)',
  }),
});

// ─── Label flutuante estilo MUI outlined ─────────────────────────────────────
const SelectWrapper = styled.div`
  position: relative;
`;

const FloatingLabel = styled.label`
  position: absolute;
  top: -9px;
  left: 8px;
  font-size: 12px;
  line-height: 1;
  padding: 0 4px;
  background: white;
  color: rgba(0, 0, 0, 0.6);
  pointer-events: auto;
  z-index: 1;
`;

// ─── sx helper: garante altura uniforme nos TextInputs ───────────────────────
const fieldSx = {
  '& .MuiInputBase-root': { height: `${FIELD_HEIGHT}px` },
};

// ─── Normalizações ────────────────────────────────────────────────────────────
const normalizeForSearch = (str: string): string =>
  String(str ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const normalizeCnpj = (value: unknown): string =>
  String(value ?? '').replace(/\D/g, '');

const onlyDigits = (value: unknown): string =>
  String(value ?? '').replace(/\D/g, '');

const safeText = (value: unknown): string => String(value ?? '').trim();

const normalizeSap = (value: unknown): string =>
  onlyDigits(value).replace(/^0+/, '');

const maskBirthDateInput = (value: unknown): string => {
  const digits = onlyDigits(value).slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const formatBirthDateForInput = (value: unknown): string => {
  const text = safeText(value);
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }

  return maskBirthDateInput(text);
};

const getBirthDateError = (value: unknown): string => {
  const text = safeText(value);
  if (!text) return '';

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const brMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (!isoMatch && !brMatch) {
    return 'Informe uma data de nascimento válida.';
  }

  const year = Number(isoMatch?.[1] ?? brMatch?.[3]);
  const month = Number(isoMatch?.[2] ?? brMatch?.[2]);
  const day = Number(isoMatch?.[3] ?? brMatch?.[1]);
  const date = new Date(year, month - 1, day);
  const isValidCalendarDate =
    year > 0 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;

  if (!isValidCalendarDate) {
    return 'Informe uma data de nascimento válida.';
  }

  const today = new Date();
  const inputDate = year * 10000 + month * 100 + day;
  const currentDate =
    today.getFullYear() * 10000 +
    (today.getMonth() + 1) * 100 +
    today.getDate();

  return inputDate > currentDate
    ? 'Informe uma data de nascimento válida.'
    : '';
};

const firstFilledValue = (...values: unknown[]) =>
  values.find(
    (value) =>
      value !== undefined && value !== null && String(value).trim() !== ''
  ) ?? '';

const normalizeSearchPatient = (patient: SearchPatientLike) => {
  const raw = patient?.raw ?? {};

  return {
    ...patient,
    name: safeText(patient?.name ?? raw?.NAME1),
    sap: normalizeSap(patient?.sap ?? patient?.sapCustomer ?? raw?.KUNNR),
    cpf: onlyDigits(patient?.cpf ?? raw?.STCD2),
    cnpj: onlyDigits(
      firstFilledValue(
        patient?.cnpj,
        patient?.companyCnpj,
        raw?.EMPRESA_CNPJ,
        raw?.STCD1
      )
    ),
    cellphone: onlyDigits(
      firstFilledValue(patient?.cellphone, patient?.phone, raw?.TELF1)
    ),
    cellphone2: onlyDigits(
      firstFilledValue(patient?.cellphone2, patient?.phone2, raw?.TELF2)
    ),
    cep: safeText(patient?.cep),
    city: safeText(patient?.city),
    uf: safeText(patient?.uf),
    address: safeText(patient?.address),
    number: safeText(patient?.number),
    neighborhood: safeText(
      patient?.neighborhood ?? patient?.delivery_neighborhood
    ),
    addressLine2: safeText(patient?.addressLine2 ?? patient?.complement),
    birthDate: safeText(patient?.birthDate ?? patient?.birth_date),
  };
};

const getPatientsFromSearchResponse = (body: {
  data?: unknown;
  oracleData?: unknown;
}) => {
  return Array.isArray(body?.oracleData) ? body.oracleData : [];
};

const normalizeCep = (value: unknown): string => {
  const cep = onlyDigits(value);

  if (cep.length !== 8) {
    return safeText(value);
  }

  return `${cep.slice(0, 5)}-${cep.slice(5)}`;
};

const parseCnpjList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeCnpj(item)).filter(Boolean);
  }

  const text = String(value ?? '').trim();
  if (!text) return [];

  return text
    .split(/[;,|\s]+/)
    .map((item) => normalizeCnpj(item))
    .filter(Boolean);
};

const isEmpty = (value: unknown): boolean => String(value ?? '').trim() === '';

const isEnterpriseSap = (value: unknown): boolean => {
  const sap = normalizeSap(value);
  return sap.startsWith('1') || sap.startsWith('6');
};

const isValidCpf = (value: unknown): boolean => {
  const digits = onlyDigits(value);
  return digits.length === 11 && !/^(\d)\1+$/.test(digits);
};

const isValidCnpj = (value: unknown): boolean =>
  onlyDigits(value).length === 14;

const isValidCep = (value: unknown): boolean => onlyDigits(value).length === 8;

const isValidPhone = (value: unknown): boolean => {
  const digits = onlyDigits(value);
  return digits.length >= 10 && digits.length <= 12;
};

type CompanyOption = {
  value: number;
  label: string;
  cnpj: string;
  empresa: string;
};

type PatientFormErrors = {
  name?: string;
  cpf?: string;
  cellphone?: string;
  phoneNumber?: string;
  cep?: string;
  city?: string;
  uf?: string;
  address?: string;
  number?: string;
  neighborhood?: string;
  addressLine2?: string;
  cnpj?: string;
  birthDate?: string;
};

type PatientFieldKey = keyof PatientFormErrors;
type SensitiveFieldKey = PatientFieldKey;

type SearchPatientLike = {
  name?: unknown;
  sap?: unknown;
  sapCustomer?: unknown;
  cpf?: unknown;
  cnpj?: unknown;
  companyCnpj?: unknown;
  cellphone?: unknown;
  cellphone2?: unknown;
  phone?: unknown;
  phone2?: unknown;
  cep?: unknown;
  city?: unknown;
  uf?: unknown;
  address?: unknown;
  number?: unknown;
  neighborhood?: unknown;
  delivery_neighborhood?: unknown;
  addressLine2?: unknown;
  complement?: unknown;
  birthDate?: unknown;
  birth_date?: unknown;
  raw?: {
    TELF1?: unknown;
    TELF2?: unknown;
    KUNNR?: unknown;
    STCD1?: unknown;
    EMPRESA_CNPJ?: unknown;
    STCD2?: unknown;
    NAME1?: unknown;
  };
};

// ─── Componente ───────────────────────────────────────────────────────────────
export const PatientsForm = ({
  disabled = false,
  canEditManagementFields = false,
  sensitiveFieldKeys = [],
}: {
  disabled?: boolean;
  canEditManagementFields?: boolean;
  sensitiveFieldKeys?: string[];
}) => {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const currentUser = usePortalStore((state: any) => state.currentUser);
  const setCurrentAlertError = usePortalStore(
    (state: any) => state.setCurrentAlertError
  );

  const name = usePatientsStore((state: any) => state.name);
  const email = usePatientsStore((state: any) => state.email);
  const sap = usePatientsStore((state: any) => state.sap);
  const cpf = usePatientsStore((state: any) => state.cpf);
  const birthDate = usePatientsStore((state: any) => state.birthDate);
  const cep = usePatientsStore((state: any) => state.cep);
  const city = usePatientsStore((state: any) => state.city);
  const uf = usePatientsStore((state: any) => state.uf);
  const address = usePatientsStore((state: any) => state.address);
  const addressLine2 = usePatientsStore((state: any) => state.addressLine2);
  const neighborhood = usePatientsStore((state: any) => state.neighborhood);
  const cellphone = usePatientsStore((state: any) => state.cellphone);
  const phoneNumber = usePatientsStore((state: any) => state.phoneNumber);
  const number = usePatientsStore((state: any) => state.number);
  const newPat = usePatientsStore((state: any) => state.newPat);
  const cnpj = usePatientsStore((state: any) => state.cnpj);
  const additionalInformation = usePatientsStore(
    (state: any) => state.additionalInformation
  );
  const additionalInformationList = usePatientsStore(
    (state: any) => state.additionalInformationList
  );

  const setName = usePatientsStore((state: any) => state.setName);
  const setEmail = usePatientsStore((state: any) => state.setEmail);
  const setSap = usePatientsStore((state: any) => state.setSap);
  const setCpf = usePatientsStore((state: any) => state.setCpf);
  const setBirthDate = usePatientsStore((state: any) => state.setBirthDate);
  const setUF = usePatientsStore((state: any) => state.setUF);
  const setCEP = usePatientsStore((state: any) => state.setCEP);
  const setCity = usePatientsStore((state: any) => state.setCity);
  const setAddress = usePatientsStore((state: any) => state.setAddress);
  const setAddressLine2 = usePatientsStore(
    (state: any) => state.setAddressLine2
  );
  const setNeighborhood = usePatientsStore(
    (state: any) => state.setNeighborhood
  );
  const setNumber = usePatientsStore((state: any) => state.setNumber);
  const setCellphone = usePatientsStore((state: any) => state.setCellphone);
  const setPhoneNumber = usePatientsStore((state: any) => state.setPhoneNumber);
  const setCNPJ = usePatientsStore((state: any) => state.setCNPJ);
  const setCompanyName = usePatientsStore((state: any) => state.setCompanyName);
  const setAdditionalInformation = usePatientsStore(
    (state: any) => state.setAdditionalInformation
  );
  const setPatientAutofilledFromDatalake = usePatientsStore(
    (state: any) => state.setPatientAutofilledFromDatalake
  );

  const getCEP = usePatientsStore((state: any) => state.getCEP);
  const getAdditionalInformationList = usePatientsStore(
    (state: any) => state.getAdditionalInformationList
  );
  const fetchPatientBySapSearch = usePatientsStore(
    (state: any) => state.fetchPatientBySapSearch
  );

  const [allowedCompanyOptions, setAllowedCompanyOptions] = React.useState<
    CompanyOption[]
  >([]);
  const [companyOptions, setCompanyOptions] = React.useState<CompanyOption[]>(
    []
  );
  const [selectedCompany, setSelectedCompany] =
    React.useState<CompanyOption | null>(null);
  const [isSapLookupLoading, setIsSapLookupLoading] = React.useState(false);
  const [linkedSapCnpjs, setLinkedSapCnpjs] = React.useState<string[]>([]);
  const [sapCnpjError, setSapCnpjError] = React.useState('');
  const [companyOptionsLoaded, setCompanyOptionsLoaded] = React.useState(false);

  //  controla erros visuais por campo.
  const [errors, setErrors] = React.useState<PatientFormErrors>({});
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});

  const lastSapLookupRef = React.useRef('');
  const sapLookupTimeoutRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const { companies } = useCompaniesCatalog();

  const canSeeWhiteMartinsFields = Boolean(
    currentUser.isAdmin || currentUser.isWhiteMartins
  );
  const showSapField = canSeeWhiteMartinsFields;
  const gridCols = isTabletOrMobile ? 1 : 2;
  const sensitiveFieldSet = React.useMemo(
    () => new Set(sensitiveFieldKeys),
    [sensitiveFieldKeys]
  );

  const getDecoratedLabel = (
    fieldName: PatientFieldKey,
    label: string,
    required = false
  ) => {
    const requiredSuffix = required ? ' *' : '';

    return `${label}${requiredSuffix}`;
  };

  const getSensitiveInputProps = (fieldName: SensitiveFieldKey) => {
    if (!sensitiveFieldSet.has(fieldName)) {
      return undefined;
    }

    return {
      endAdornment: (
        <InputAdornment position="end">
          <Tooltip
            title={SENSITIVE_FIELD_TOOLTIP}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <InfoIcon fontSize="small" />
          </Tooltip>
        </InputAdornment>
      ),
    };
  };

  const getFieldSx = (fieldName: SensitiveFieldKey, base = fieldSx) => {
    if (!sensitiveFieldSet.has(fieldName)) {
      return base;
    }

    return {
      '& .MuiFormHelperText-root': {
        marginLeft: 0,
      },
    };
  };

  const isCnpjAvailableInPortal = React.useCallback(
    (value: unknown) => {
      const normalized = normalizeCnpj(value);

      if (newPat && normalizeSap(sap)) {
        return (
          linkedSapCnpjs.includes(normalized) &&
          companyOptions.some(
            (option) => normalizeCnpj(option.cnpj) === normalized
          )
        );
      }

      if (companyOptions.length === 0) return true;
      return companyOptions.some(
        (option) => normalizeCnpj(option.cnpj) === normalized
      );
    },
    [companyOptions, linkedSapCnpjs, newPat, sap]
  );

  const validateField = React.useCallback(
    (fieldName: keyof PatientFormErrors, value?: unknown): string => {
      const values: Record<keyof PatientFormErrors, unknown> = {
        name,
        cpf,
        cellphone,
        phoneNumber,
        cep,
        city,
        uf,
        address,
        number,
        neighborhood,
        addressLine2,
        cnpj,
        birthDate,
      };

      const fieldValue = value ?? values[fieldName];

      switch (fieldName) {
        case 'name':
          return isEmpty(fieldValue)
            ? 'Informe o nome completo do paciente.'
            : '';

        case 'cpf':
          if (isEnterpriseSap(sap)) return '';
          if (isEmpty(fieldValue)) return 'Informe o CPF do paciente.';
          return isValidCpf(fieldValue)
            ? ''
            : 'CPF deve conter 11 dígitos válidos.';

        case 'cellphone':
          if (isEmpty(fieldValue))
            return 'Informe o telefone/celular do paciente.';
          return isValidPhone(fieldValue)
            ? ''
            : 'Telefone/celular deve conter DDD e ter entre 10 e 12 dígitos.';

        case 'phoneNumber':
          if (isEmpty(fieldValue)) return '';
          return isValidPhone(fieldValue)
            ? ''
            : 'Telefone 2 deve conter DDD e ter entre 10 e 12 dígitos.';

        case 'cep':
          if (isEmpty(fieldValue)) return 'Informe o CEP.';
          return isValidCep(fieldValue) ? '' : 'CEP deve conter 8 dígitos.';

        case 'city':
          return isEmpty(fieldValue) ? 'Informe a cidade.' : '';

        case 'uf':
          if (isEmpty(fieldValue)) return 'Informe a UF.';
          return String(fieldValue).trim().length === 2
            ? ''
            : 'UF deve conter 2 letras.';

        case 'address':
          return isEmpty(fieldValue) ? 'Informe o endereço.' : '';

        case 'number':
          return isEmpty(fieldValue) ? 'Informe o número.' : '';

        case 'cnpj':
          if (newPat && normalizeSap(sap) && sapCnpjError) return sapCnpjError;
          if (isEmpty(fieldValue)) return 'Selecione a empresa/CNPJ.';
          if (!isValidCnpj(fieldValue)) return 'CNPJ deve conter 14 dígitos.';
          return isCnpjAvailableInPortal(fieldValue)
            ? ''
            : 'Preencha com um CNPJ válido no portal.';

        case 'birthDate':
          if (newPat && isEmpty(fieldValue))
            return 'Informe a data de nascimento do paciente.';
          return getBirthDateError(fieldValue);

        default:
          return '';
      }
    },
    [
      name,
      cpf,
      cellphone,
      phoneNumber,
      cep,
      city,
      uf,
      address,
      number,
      neighborhood,
      addressLine2,
      cnpj,
      birthDate,
      newPat,
      sap,
      sapCnpjError,
      isCnpjAvailableInPortal,
    ]
  );

  const validateAllVisibleFields = React.useCallback(() => {
    const fields: Array<keyof PatientFormErrors> = [
      'name',
      'cpf',
      'cellphone',
      'phoneNumber',
      'cep',
      'city',
      'uf',
      'address',
      'number',
      'cnpj',
      'birthDate',
    ];

    const nextErrors = fields.reduce((acc, field) => {
      const message = validateField(field);
      if (message) acc[field] = message;
      return acc;
    }, {} as PatientFormErrors);

    setErrors(nextErrors);

    return nextErrors;
  }, [validateField]);

  const touchField = (fieldName: keyof PatientFormErrors) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));

    const message = validateField(fieldName);
    setErrors((prev) => ({
      ...prev,
      [fieldName]: message,
    }));
  };

  const getFieldError = (fieldName: keyof PatientFormErrors) => {
    if (!touched[fieldName]) return '';
    return errors[fieldName] ?? '';
  };

  const handleFieldChange = (
    fieldName: keyof PatientFormErrors,
    value: string,
    setter: (value: string) => void
  ) => {
    setter(value);

    if (touched[fieldName]) {
      const message = validateField(fieldName, value);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: message,
      }));
    }
  };

  // ── CEP lookup ──────────────────────────────────────────────────────────────
  const addressByCEP = async (cepRaw: string) => {
    const cepClean = cepRaw.trim().replace(/\D/g, '');

    handleFieldChange('cep', cepClean, setCEP);

    if (cepClean.length === 8) {
      const fullAddress = await getCEP(cepRaw);

      if (fullAddress.erro) {
        setCurrentAlertError(
          'CEP fornecido não está ligado a nenhum endereço. Preencha o formulário com o endereço desejado.'
        );
      } else {
        setAddress(fullAddress.logradouro);
        setNeighborhood(fullAddress.bairro ?? '');
        setCity(fullAddress.localidade);
        setUF(fullAddress.uf);

        setErrors((prev) => ({
          ...prev,
          city: '',
          uf: '',
          address: '',
        }));
      }
    } else {
      setAddress('');
      setNeighborhood('');
      setCity('');
      setUF('');
    }
  };

  const fillFormWithPatient = React.useCallback(
    (patient: SearchPatientLike) => {
      if (!patient) return;

      const normalizedPatient = normalizeSearchPatient(patient);
      setPatientAutofilledFromDatalake(true);

      setName(normalizedPatient.name);
      setSap(normalizedPatient.sap);

      if (isValidCpf(normalizedPatient.cpf)) {
        setCpf(normalizedPatient.cpf);
      } else {
        setCpf('');
      }

      setCellphone(normalizedPatient.cellphone);
      setPhoneNumber(normalizedPatient.cellphone2);
      setCEP(normalizedPatient.cep ? normalizeCep(normalizedPatient.cep) : '');
      setCity(normalizedPatient.city);
      setUF(normalizedPatient.uf);
      setAddress(normalizedPatient.address);
      setNumber(normalizedPatient.number);
      setNeighborhood(normalizedPatient.neighborhood);
      setAddressLine2(normalizedPatient.addressLine2);

      if (normalizedPatient.birthDate) {
        setBirthDate(normalizedPatient.birthDate);
      }

      setTimeout(() => {
        validateAllVisibleFields();
      }, 0);
    },
    [
      setName,
      setSap,
      setCpf,
      setCellphone,
      setPhoneNumber,
      setCEP,
      setCity,
      setUF,
      setAddress,
      setAddressLine2,
      setNumber,
      setNeighborhood,
      setBirthDate,
      validateAllVisibleFields,
    ]
  );

  // ── Busca automática por SAP com debounce ───────────────────────────────────
  React.useEffect(() => {
    if (!newPat) return;

    const normalizedSap = onlyDigits(sap);

    if (sapLookupTimeoutRef.current) {
      clearTimeout(sapLookupTimeoutRef.current);
    }

    if (!normalizedSap) {
      setIsSapLookupLoading(false);
      setLinkedSapCnpjs([]);
      setSapCnpjError('');
      setSelectedCompany(null);
      setCNPJ('');
      setCompanyName('');
      lastSapLookupRef.current = '';
      return;
    }

    if (normalizedSap === lastSapLookupRef.current) {
      return;
    }

    setLinkedSapCnpjs([]);
    setSapCnpjError('');
    setCompanyOptions([]);
    setSelectedCompany(null);
    setCNPJ('');
    setCompanyName('');

    if (normalizedSap.length < 4) {
      setIsSapLookupLoading(false);
      return;
    }

    sapLookupTimeoutRef.current = setTimeout(async () => {
      lastSapLookupRef.current = normalizedSap;
      setIsSapLookupLoading(true);

      try {
        const resp = await fetchPatientBySapSearch(null, normalizedSap);

        if (
          normalizeSap(usePatientsStore.getState().sap) !==
          normalizeSap(normalizedSap)
        ) {
          return;
        }

        const patients = getPatientsFromSearchResponse(resp?.body);

        if (
          resp?.httpStatus === 200 &&
          Array.isArray(patients) &&
          patients.length > 0
        ) {
          const exactPatients = patients.filter(
            (patient: SearchPatientLike) => {
              return (
                normalizeSap(
                  patient.sap ?? patient.sapCustomer ?? patient?.raw?.KUNNR
                ) === normalizedSap
              );
            }
          );
          const exactPatient =
            exactPatients.find(
              (patient: SearchPatientLike) =>
                normalizeSearchPatient(patient).cnpj
            ) ?? exactPatients[0];

          if (!exactPatient) {
            setSapCnpjError(
              'O código SAP não possui vínculo válido no Datalake.'
            );
            return;
          }

          const linkedCnpjs = Array.from(
            new Set(
              exactPatients
                .map(
                  (patient: SearchPatientLike) =>
                    normalizeSearchPatient(patient).cnpj
                )
                .filter(Boolean)
            )
          );

          if (linkedCnpjs.length === 0) {
            setSapCnpjError(
              'O código SAP não possui CNPJ vinculado no Datalake.'
            );
          } else {
            setLinkedSapCnpjs(linkedCnpjs);
            fillFormWithPatient(exactPatient);
          }
        } else {
          setSapCnpjError(
            'O código SAP não possui vínculo válido no Datalake.'
          );
        }
      } catch (error) {
        console.error('Erro ao buscar paciente pelo SAP:', error);
        setSapCnpjError(
          'Não foi possível validar o vínculo do código SAP no Datalake.'
        );
      } finally {
        setIsSapLookupLoading(false);
      }
    }, 700);

    return () => {
      if (sapLookupTimeoutRef.current) {
        clearTimeout(sapLookupTimeoutRef.current);
      }
    };
  }, [sap, newPat]);

  // ── Carrega empresas e resolve seleção inicial ──────────────────────────────
  React.useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const allCompanies = Array.isArray(companies) ? companies : [];

        const userCompaniesResponse = await listUserCompanies().catch(() => []);
        const linkedCompanies = Array.isArray(userCompaniesResponse)
          ? userCompaniesResponse
          : Array.isArray((userCompaniesResponse as any)?.data)
          ? (userCompaniesResponse as any).data
          : Array.isArray((userCompaniesResponse as any)?.body)
          ? (userCompaniesResponse as any).body
          : [];

        const canSeeAllCnpjs = Boolean(
          currentUser?.isAdmin || currentUser?.canSeeAllCnpjs
        );

        const linkedCnpjs = new Set<string>([
          ...linkedCompanies
            .map((item: any) => normalizeCnpj(item?.cnpj))
            .filter(Boolean),
          ...parseCnpjList(currentUser?.cnpj),
        ]);

        const companiesToUse = canSeeAllCnpjs
          ? allCompanies
          : allCompanies.filter((company: any) =>
              linkedCnpjs.has(normalizeCnpj(company?.cnpj))
            );

        const options: CompanyOption[] = (companiesToUse ?? []).map(
          (c: any) => {
            const cnpjFormatted = String(c.cnpj ?? '').replace(
              /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
              '$1.$2.$3/$4-$5'
            );

            return {
              value: c.id,
              label:
                c.empresa && cnpjFormatted
                  ? `${c.empresa} — ${cnpjFormatted}`
                  : c.empresa || cnpjFormatted,
              cnpj: c.cnpj,
              empresa: c.empresa,
            };
          }
        );

        setAllowedCompanyOptions(options);
        setCompanyOptionsLoaded(true);
      } catch (err) {
        console.error('Failed to fetch companies:', err);
        setCompanyOptionsLoaded(true);
      }
    };

    if (!Array.isArray(companies) || companies.length === 0) {
      return;
    }

    fetchCompanies();
  }, [companies, currentUser]);

  React.useEffect(() => {
    const normalizedSap = normalizeSap(sap);

    if (!newPat || !normalizedSap) {
      setCompanyOptions(allowedCompanyOptions);

      if (cnpj) {
        const match = allowedCompanyOptions.find(
          (option) => normalizeCnpj(option.cnpj) === normalizeCnpj(cnpj)
        );
        setSelectedCompany(match ?? null);
        setCompanyName(match?.empresa ?? '');
      } else {
        setSelectedCompany(null);
        setCompanyName('');
      }
      return;
    }

    if (linkedSapCnpjs.length === 0 || !companyOptionsLoaded) {
      setCompanyOptions([]);
      setSelectedCompany(null);
      setCompanyName('');
      return;
    }

    const linkedOptions = allowedCompanyOptions.filter((option) =>
      linkedSapCnpjs.includes(normalizeCnpj(option.cnpj))
    );

    if (linkedOptions.length === 0) {
      setCompanyOptions([]);
      setSelectedCompany(null);
      setCNPJ('');
      setCompanyName('');
      setSapCnpjError(
        'Nenhum CNPJ vinculado ao código SAP é permitido para este usuário.'
      );
      return;
    }

    setCompanyOptions(linkedOptions);

    const selectedAllowedOption = linkedOptions.find(
      (option) => normalizeCnpj(option.cnpj) === normalizeCnpj(cnpj)
    );
    const nextSelected =
      selectedAllowedOption ??
      (linkedOptions.length === 1 ? linkedOptions[0] : null);

    setSelectedCompany(nextSelected);
    setCNPJ(nextSelected?.cnpj ?? '');
    setCompanyName(nextSelected?.empresa ?? '');
    setSapCnpjError('');
    setErrors((prev) => ({
      ...prev,
      cnpj: nextSelected ? '' : 'Selecione a empresa/CNPJ.',
    }));
  }, [
    allowedCompanyOptions,
    cnpj,
    companyOptionsLoaded,
    linkedSapCnpjs,
    newPat,
    sap,
    setCNPJ,
    setCompanyName,
  ]);

  // ── Sincroniza quando cnpj do store muda externamente ──────────────────────
  React.useEffect(() => {
    if (!cnpj) {
      setSelectedCompany(null);
      setCompanyName('');
      return;
    }

    if (companyOptions.length === 0) return;

    const match = companyOptions.find(
      (o) => normalizeCnpj(o.cnpj) === normalizeCnpj(cnpj)
    );
    setSelectedCompany(match ?? null);
    setCompanyName(match?.empresa ?? '');
  }, [cnpj, companyOptions, setCompanyName]);

  React.useEffect(() => {
    getAdditionalInformationList().catch(console.error);
  }, [getAdditionalInformationList]);

  const handleSelectCompany = (option: CompanyOption | null) => {
    if (newPat && normalizeSap(sap) && companyOptions.length <= 1) {
      return;
    }

    setSelectedCompany(option);
    const nextCnpj = option ? option.cnpj : '';
    const nextCompanyName = option ? option.empresa : '';
    setCNPJ(nextCnpj);
    setCompanyName(nextCompanyName);

    setTouched((prev) => ({ ...prev, cnpj: true }));
    setErrors((prev) => ({
      ...prev,
      cnpj: validateField('cnpj', nextCnpj),
    }));
  };

  return (
    <FormWrapper>
      {/* ── Identificação ─────────────────────────────────────────────────── */}
      <Section>
        <SectionLabel>Identificação</SectionLabel>
        <Grid cols={gridCols}>
          <TextInput
            value={name}
            label={getDecoratedLabel('name', 'Nome Completo', true)}
            handleChange={(e) =>
              handleFieldChange('name', e.target.value, setName)
            }
            onBlur={() => touchField('name')}
            disabled={disabled}
            sx={getFieldSx('name')}
            error={Boolean(getFieldError('name'))}
            helperText={getFieldError('name')}
            InputProps={getSensitiveInputProps('name')}
          />

          {showSapField && (
            <TextInput
              value={sap}
              label="SAP (Opcional)"
              handleChange={(e) => {
                const nextSap = e.target.value;
                setSap(nextSap);

                if (newPat) {
                  setLinkedSapCnpjs([]);
                  setSapCnpjError('');
                  setCompanyOptions([]);
                  setSelectedCompany(null);
                  setCNPJ('');
                  setCompanyName('');
                  lastSapLookupRef.current = '';
                }
              }}
              disabled={disabled}
              sx={fieldSx}
              InputProps={{
                endAdornment: isSapLookupLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : undefined,
              }}
            />
          )}

          <TextInput
            value={formatBirthDateForInput(birthDate)}
            label={getDecoratedLabel(
              'birthDate',
              'Data de nascimento (DD/MM/AAAA)',
              newPat
            )}
            placeholder="DD/MM/AAAA"
            handleChange={(e) => {
              const nextValue = maskBirthDateInput(e.target.value);
              handleFieldChange('birthDate', nextValue, setBirthDate);
            }}
            onBlur={() => touchField('birthDate')}
            disabled={disabled}
            sx={getFieldSx('birthDate')}
            error={Boolean(getFieldError('birthDate'))}
            helperText={getFieldError('birthDate')}
            inputProps={{ maxLength: 10, inputMode: 'numeric' }}
          />

          <TextInput
            value={cpf}
            label={getDecoratedLabel('cpf', 'CPF', !isEnterpriseSap(sap))}
            handleChange={(e) => {
              setPatientAutofilledFromDatalake(false);
              handleFieldChange('cpf', e.target.value, setCpf);
            }}
            onBlur={() => touchField('cpf')}
            disabled={disabled}
            sx={getFieldSx('cpf')}
            error={Boolean(getFieldError('cpf'))}
            helperText={getFieldError('cpf')}
            InputProps={getSensitiveInputProps('cpf')}
          />
        </Grid>
      </Section>

      {/* ── Contato ───────────────────────────────────────────────────────── */}
      <Section>
        <SectionLabel>Contato</SectionLabel>
        <Grid cols={gridCols}>
          <TextInput
            value={cellphone}
            label={getDecoratedLabel('cellphone', 'Telefone 1 (com DDD)', true)}
            handleChange={(e) =>
              handleFieldChange('cellphone', e.target.value, setCellphone)
            }
            onBlur={() => touchField('cellphone')}
            disabled={disabled}
            sx={getFieldSx('cellphone')}
            error={Boolean(getFieldError('cellphone'))}
            helperText={getFieldError('cellphone')}
            InputProps={getSensitiveInputProps('cellphone')}
          />

          <TextInput
            value={phoneNumber}
            label="Telefone 2 (Opcional)"
            handleChange={(e) =>
              handleFieldChange('phoneNumber', e.target.value, setPhoneNumber)
            }
            onBlur={() => touchField('phoneNumber')}
            disabled={disabled}
            sx={getFieldSx('phoneNumber')}
            error={Boolean(getFieldError('phoneNumber'))}
            helperText={getFieldError('phoneNumber')}
            InputProps={getSensitiveInputProps('phoneNumber')}
          />

          <TextInput
            value={email}
            label="Email (Opcional)"
            handleChange={(e) => setEmail(e.target.value)}
            disabled={disabled}
            sx={fieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title="O e-mail deve pertencer ao paciente ou a um familiar"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Section>

      {/* ── Endereço ──────────────────────────────────────────────────────── */}
      <Section>
        <SectionLabel>Endereço</SectionLabel>
        <Grid cols={gridCols}>
          <TextInput
            value={cep}
            label={getDecoratedLabel('cep', 'CEP', true)}
            handleChange={(e) => addressByCEP(e.target.value)}
            onBlur={() => touchField('cep')}
            disabled={disabled}
            sx={getFieldSx('cep')}
            error={Boolean(getFieldError('cep'))}
            helperText={getFieldError('cep')}
            InputProps={getSensitiveInputProps('cep')}
          />

          <TextInput
            value={city}
            label={getDecoratedLabel('city', 'Cidade', true)}
            handleChange={(e) =>
              handleFieldChange('city', e.target.value, setCity)
            }
            onBlur={() => touchField('city')}
            disabled={disabled}
            sx={getFieldSx('city')}
            error={Boolean(getFieldError('city'))}
            helperText={getFieldError('city')}
            InputProps={getSensitiveInputProps('city')}
          />

          <TextInput
            value={uf}
            label={getDecoratedLabel('uf', 'UF', true)}
            handleChange={(e) => handleFieldChange('uf', e.target.value, setUF)}
            onBlur={() => touchField('uf')}
            disabled={disabled}
            sx={getFieldSx('uf')}
            error={Boolean(getFieldError('uf'))}
            helperText={getFieldError('uf')}
            InputProps={getSensitiveInputProps('uf')}
          />

          <TextInput
            value={address}
            label={getDecoratedLabel('address', 'Endereco', true)}
            handleChange={(e) =>
              handleFieldChange('address', e.target.value, setAddress)
            }
            onBlur={() => touchField('address')}
            disabled={disabled}
            sx={getFieldSx('address')}
            error={Boolean(getFieldError('address'))}
            helperText={getFieldError('address')}
            InputProps={getSensitiveInputProps('address')}
          />

          <TextInput
            value={neighborhood}
            label="Bairro"
            handleChange={(e) =>
              handleFieldChange('neighborhood', e.target.value, setNeighborhood)
            }
            disabled={disabled}
            sx={getFieldSx('neighborhood')}
            InputProps={getSensitiveInputProps('neighborhood')}
          />

          <TextInput
            value={number}
            label={getDecoratedLabel('number', 'Numero', true)}
            handleChange={(e) =>
              handleFieldChange('number', e.target.value, setNumber)
            }
            onBlur={() => touchField('number')}
            disabled={disabled}
            sx={getFieldSx('number')}
            error={Boolean(getFieldError('number'))}
            helperText={getFieldError('number')}
            InputProps={getSensitiveInputProps('number')}
          />

          <TextInput
            value={addressLine2}
            label="Complemento (Opcional)"
            handleChange={(e) => setAddressLine2(e.target.value)}
            disabled={disabled}
            sx={getFieldSx('addressLine2')}
            InputProps={getSensitiveInputProps('addressLine2')}
          />
        </Grid>
      </Section>

      {/* ── Vínculo ───────────────────────────────────────────────────────── */}
      <Section>
        <SectionLabel>Vínculo</SectionLabel>
        <Grid cols={gridCols}>
          <SelectWrapper>
            <FloatingLabel>
              {getDecoratedLabel('cnpj', 'Empresa / CNPJ', false)}
              <RequiredMark>*</RequiredMark>
              {sensitiveFieldSet.has('cnpj') && (
                <Tooltip
                  title={SENSITIVE_FIELD_TOOLTIP}
                  sx={{ display: 'inline-flex', marginLeft: '4px' }}
                >
                  <InfoIcon
                    style={{ fontSize: '14px', verticalAlign: 'middle' }}
                  />
                </Tooltip>
              )}
            </FloatingLabel>

            <ReactSelect
              options={companyOptions}
              value={selectedCompany}
              onChange={handleSelectCompany}
              onBlur={() => touchField('cnpj')}
              placeholder="Buscar por empresa ou CNPJ..."
              isSearchable
              isClearable={
                !newPat || !normalizeSap(sap) || companyOptions.length > 1
              }
              isDisabled={
                disabled ||
                Boolean(
                  newPat && normalizeSap(sap) && companyOptions.length <= 1
                )
              }
              menuPortalTarget={document.body}
              styles={buildSelectStyles(
                disabled ||
                  Boolean(
                    newPat && normalizeSap(sap) && companyOptions.length <= 1
                  ),
                Boolean(getFieldError('cnpj') || sapCnpjError)
              )}
              noOptionsMessage={() =>
                sapCnpjError || 'Nenhuma empresa encontrada'
              }
              filterOption={(option, inputValue) => {
                if (!inputValue) return true;
                return normalizeForSearch(option.label).includes(
                  normalizeForSearch(inputValue)
                );
              }}
            />

            {(getFieldError('cnpj') || sapCnpjError) && (
              <FieldErrorText>
                {getFieldError('cnpj') || sapCnpjError}
              </FieldErrorText>
            )}
          </SelectWrapper>
        </Grid>
      </Section>

      {/* ── Classificação (apenas admins/WM) ─────────────────────────────── */}
      {canSeeWhiteMartinsFields && (
        <Section>
          <SectionLabel>Classificação</SectionLabel>

          <MultipleSelect
            label="Informações adicionais"
            items={additionalInformationList}
            value={additionalInformation}
            onChange={(e) =>
              setAdditionalInformation(e.target.value as string[])
            }
            disabled={disabled || !canEditManagementFields}
          />
        </Section>
      )}
    </FormWrapper>
  );
};
